import { getSubscriptionPlans } from "api/subscriptions";
import type { SubscriptionPlanDto } from "api/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CURRENCY_BY_REGION, detectPricingRegion, formatAmount, formatPrice, PricingRegion } from "utilities";

/** Display-ready Pro pricing for the pages that are shown to signed-out visitors. */
export type PublicPricing = {
	/** Plan name as it appears on the invoice, e.g. "Pro". */
	name: string;
	/** Amount debited each billing cycle, with its currency symbol — the GST-inclusive total. */
	total: string;
	/** ISO code of the currency the card is charged in, e.g. "INR". */
	currencyCode: string;
	/** The same currency spelled out for billing copy, e.g. "Indian Rupees (INR)". */
	currencyLabel: string;
	/** Portion of the total before GST, with its currency symbol. */
	base: string;
	/** GST contained in the total, with its currency symbol. */
	tax: string;
	/** GST percentage as a plain number, e.g. "18". */
	taxRate: string;
	/**
	 * Whether the price actually contains tax. False for a sale outside India, which is a zero-rated
	 * export — every "GST included" string has to be suppressed in that case, or the page asserts a
	 * tax the customer is not charged.
	 */
	hasTax: boolean;
	/** Zero in the charged currency, for the Free tier — "₹0" or "USD 0", never a hardcoded symbol. */
	zero: string;
	/** Region the price was picked for, inferred from the browser time zone. */
	region: PricingRegion;
	/** False while the built-in fallback figures are showing instead of live gateway figures. */
	isLive: boolean;
};

/**
 * Last known Razorpay monthly plan, used until the live figures arrive and if the API cannot be
 * reached at all. A payment gateway reviewing the site must never land on a blank price, so the
 * page always renders a number — keep this in step with the Razorpay plan amount.
 */
const FALLBACK_PLAN: SubscriptionPlanDto = {
	amount: 59,
	baseAmount: 50,
	currency: "INR",
	id: "fallback-monthly",
	interval: 1,
	name: "Pro",
	period: "monthly",
	taxAmount: 9,
	taxRate: 18
};

/** Everything but the spelled-out currency, which is copy and so comes from the locale file. */
type PricingFigures = Omit<PublicPricing, "currencyLabel">;

const toPricing = (plan: SubscriptionPlanDto, region: PricingRegion, isLive: boolean): PricingFigures => ({
	base: formatPrice(plan.baseAmount || plan.amount, plan.currency),
	currencyCode: plan.currency.toUpperCase(),
	// The API zeroes the whole breakdown for an export sale, so a zero rate is how "no GST applies"
	// arrives here — not a missing value.
	hasTax: plan.taxAmount > 0 && plan.taxRate > 0,
	isLive,
	name: plan.name,
	region,
	tax: formatPrice(plan.taxAmount, plan.currency),
	taxRate: formatAmount(plan.taxRate),
	total: formatPrice(plan.amount, plan.currency),
	zero: formatPrice(0, plan.currency)
});

/**
 * Picks the plan priced in the region's currency. Since TTAPI-17 the endpoint returns both plans to
 * an anonymous caller, so this finally selects between them instead of always landing on the
 * fallback.
 *
 * The fallback still matters: it covers an unconfigured USD plan (clearing
 * `Razorpay:MonthlyPlanIdUsd` is the documented rollback lever, after which only INR comes back) and
 * any future currency with no plan behind it. The API lists INR first, so falling back to the first
 * plan means falling back to rupees.
 */
const pickPlanForRegion = (plans: SubscriptionPlanDto[], region: PricingRegion): SubscriptionPlanDto | undefined => {
	const wanted = CURRENCY_BY_REGION[region];

	return plans.find((plan: SubscriptionPlanDto) => plan.currency.toUpperCase() === wanted) ?? plans[0];
};

/**
 * Reads the Pro price from the public plans endpoint, which needs no sign-in, choosing the plan for
 * the region inferred from the browser time zone. Falls back to {@link FALLBACK_PLAN} while loading
 * and when the request fails, so pricing is always on screen.
 */
export const usePublicPricing = (): PublicPricing => {
	const { t } = useTranslation("pricingPage");
	const [region] = useState<PricingRegion>(detectPricingRegion);
	const [pricing, setPricing] = useState<PricingFigures>(() => toPricing(FALLBACK_PLAN, region, false));

	useEffect(() => {
		let isCurrent = true;

		getSubscriptionPlans()
			.then((plans: SubscriptionPlanDto[]) => {
				const plan = pickPlanForRegion(plans, region);

				if (isCurrent && plan) {
					setPricing(toPricing(plan, region, true));
				}
			})
			.catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [region]);

	// Spells out the charged currency so an international visitor knows what their card is billed in;
	// a currency with no name in the locale file shows as its bare code.
	return {
		...pricing,
		currencyLabel: t(`currencyNames.${pricing.currencyCode}`, { defaultValue: pricing.currencyCode })
	};
};
